import axios from 'axios'
import { Container, Singleton } from 'typescript-ioc/es5'
import { action } from 'mobx'
import { UserDto } from '../dtos/user'
import { IApiResponseSuccess } from '../models/api-response-success'
import { AppState } from '../stores/app'
import { AppService } from './app'
import { sendGAEvent } from '../components/ga-helper/ga-helper'
import { axiosFetch } from '../config/axios-setup'
import aqe, { SessionState } from '@pushly/aqe'
import { shouldUseLegacyAuthHeader } from '../_utils/utils'
import { PushlyCookie } from '../enums/pushly-cookie.enum'
import { buildLogoutRoute, buildRouteFromHostname } from '../_utils/routing'
import { getPlatformIdentitySnapshot } from '../providers/platform-identity-provider/platform-identity-provider'
import { APP_BASE_ROUTE } from '../constants'
import { resetFlagCatalog } from '../core/flags'
import { broadcastLogout } from '@pushly/aqe/lib/utils/session/session-state-sync'

@Singleton
export class AuthService {
    private appState: AppState
    private appService: AppService
    private sessionState: SessionState

    public constructor() {
        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.sessionState = Container.get(SessionState)
    }

    @action.bound
    public async login(email: string, password: string) {
        const response = await axios.post<IApiResponseSuccess<UserDto> & { token: string }>(
            `${aqe.defaults.publicApiDomain}/auth/login`,
            { email, password },
            {
                bypassGlobalErrorHandlers: true,
            } as any,
        )
        const responseData = response.data

        if (shouldUseLegacyAuthHeader()) {
            window.localStorage.setItem(PushlyCookie.PLATFORM_COOKIE, responseData.token)
        }

        this.appState.currentUser = UserDto.fromApiResponse(responseData.data)

        await this.appState.fetchUserDependencies()

        this.appService.startSession()
        sendGAEvent('set_current_user', {}, this.appState)
    }

    @action.bound
    public async logout(expired: boolean = false) {
        // Set logout flag to prevent Auth0Redirect from interfering
        // flag pertains to sessionExpiration logging out state - where expiration screen has shown
        // but the page is refreshed or re-navigated to. This prevents unintended re-authorize redirect flows
        // faux extending the session
        this.appState.isLoggingOut = true
        this.appService.setAppLoading()

        try {
            if (!expired) {
                await axiosFetch(
                    'post',
                    {
                        url: `${aqe.defaults.publicApiDomain}/auth/logout`,
                    },
                    'logout',
                )
            }
        } catch (error) {
            // The session is still live: hand the app back its loading state and its logout flag before the caller reacts.
            this.appState.isLoggingOut = false
            this.appService.unsetAppLoading()
            throw new Error(error.message)
        }

        let label = this.appState?.whiteLabelSettings
        /**
         * Currently, the only known case of appState being undefined is particular
         * API calls being made at the same time as a MissingAuthToken/ExpireAuthToken error
         * triggers a logout.
         *
         * There may be other cases where {appState.whiteLabelSettings} are unavailable and
         * the snapshot from PlatformIdentityProvider HOC should be used.
         */
        if (!label) {
            const snapshot = getPlatformIdentitySnapshot()
            if (snapshot.loaded) {
                label = snapshot.whiteLabelSettings
            }
        }

        if (!label) {
            console.error('whitelabel settings not found')
            location.assign(APP_BASE_ROUTE)
            return
        }

        this.appService.stopFetchingPlatformMessages()

        if (shouldUseLegacyAuthHeader()) {
            window.localStorage.removeItem(PushlyCookie.PLATFORM_COOKIE)
        }

        let redirectBase = buildRouteFromHostname(label?.getHostname()!)
        if (expired) {
            localStorage.setItem('refreshRedirectURI', location.pathname)
        }

        const nextRoute = buildLogoutRoute(label?.getAuth0HostedLoginDomain()!, true)
        const nextHref = [
            `${nextRoute}?client_id=${label?.getAuth0PlatformClientId()}`,
            `returnTo=${redirectBase}`,
        ].join('&')

        // Clear state before navigation
        this.appState.currentUser = undefined
        this.appState.currentUserDomains = undefined
        this.appState.stopUserDependencyRefresher()
        this.appState.accessOwner.reset()
        resetFlagCatalog()
        this.sessionState.isAuthenticated = false
        this.appState.abilityStore.flush()
        this.appService.resetSession()

        // Notify other tabs to log out
        broadcastLogout()

        const isStandalone =
            (window.navigator as any).standalone === true || window.matchMedia('(display-mode: standalone)').matches

        if (isStandalone) {
            // Use window.location.href for iOS standalone/PWA mode — .replace() has
            // different history behavior in WKWebView and can result in a blank page
            // when Auth0 redirects back to the app.
            window.location.href = nextHref
        } else {
            // Use window.location.replace() to immediately navigate away
            // The isLoggingOut flag prevents Auth0Redirect from interfering
            window.location.replace(nextHref)
        }
    }
}
